// ============================================================================
// Turn wizard answers into a readable Q&A transcript.
// ============================================================================
// When a user asks for advice on their assessment, we do NOT want the adviser
// (or the case-analysis AI) to receive a blob of form fields. They should
// receive what looks like a conversation, because that is what every other
// completed case looks like — and the analysis pipeline is built to read one.
//
// This produces the question exactly as the user was asked it and the answer
// exactly as they picked it, in their own language, straight from the same i18n
// table the wizard rendered from. The scoring summary is NOT built here: the
// server appends that from its own recomputation, so the numbers in the
// transcript are never client-supplied.
// ============================================================================

import { translator } from './i18n';
import {
  REGIONS, EXPERIENCE_OPTIONS, CPSO_CERTIFICATES, EMPLOYER_QUESTIONS,
  EMPLOYER_LOCATION_TIERS,
} from './rules';
import { resolveNoc } from './noc';

const MONTH_KEYS = { '0-5': 'months05', '6-12': 'months612', '13-24': 'months1324', '25+': 'months25' };

const EDUCATION_KEYS = {
  'doctorate': 'eduDoctorate',
  'masters': 'eduMasters',
  'univ-cert-above-bachelor': 'eduUnivAboveBachelor',
  'bachelor': 'eduBachelor',
  'ontario-college-grad-cert': 'eduOntarioGradCert',
  'univ-cert-below-bachelor': 'eduUnivBelowBachelor',
  'college-diploma': 'eduCollegeDiploma',
  'apprenticeship': 'eduApprenticeship',
  'high-school': 'eduHighSchool',
  'less-than-high-school': 'eduLessThanHighSchool',
};

const CLB_KEYS = {
  '4-or-lower': 'clb4OrLower', '5': 'clb5', '6': 'clb6', '7': 'clb7',
  '8': 'clb8', '9+': 'clb9Plus', 'untested': 'clbUntested',
};

const STATUS_KEYS = {
  'work-permit': 'statusWorkPermit', 'study-permit': 'statusStudyPermit',
  'maintained': 'statusMaintained', 'visitor': 'statusVisitor',
  'no-status': 'statusNone', 'outside-canada': 'statusOutside',
};

const EARNINGS_KEYS = {
  '70k+': 'earnings70k', '50-69999': 'earnings50to70', '30-49999': 'earnings30to50',
  'under-30k': 'earningsUnder30', 'none': 'earningsNone',
};

const PATH_KEYS = { employer: 'pathEmployer', physician: 'pathPhysician', neither: 'pathNeither' };

/**
 * @returns {Array<{q: string, a: string}>} ordered Q&A pairs, questions omitted
 *          when the branch never asked them.
 */
export function buildCandidateTranscript(answers, language = 'en') {
  const tr = translator(language);
  const pairs = [];
  const add = (questionKey, answerText) => {
    if (answerText === null || answerText === undefined || answerText === '') return;
    pairs.push({ q: tr(questionKey), a: String(answerText) });
  };

  add('pathTitle', PATH_KEYS[answers.path] ? tr(PATH_KEYS[answers.path]) : answers.path);

  if (answers.path === 'neither') return pairs;

  if (answers.path === 'physician') {
    add('ohipTitle', answers.ohip === 'yes' ? tr('yes') : tr('notYet'));
    const cpso = CPSO_CERTIFICATES.find(c => c.id === answers.cpso);
    add('cpsoTitle', cpso ? tr(cpso.labelKey) : answers.cpso);
    add('practiceTitle', MONTH_KEYS[answers.practiceMonths] ? tr(MONTH_KEYS[answers.practiceMonths]) : answers.practiceMonths);
  }

  const noc = resolveNoc(answers.noc);
  if (noc) {
    add('nocTitle', tr('nocResolved', {
      code: noc.code,
      teer: noc.teer,
      category: noc.broadCategoryLabel,
    }));
  }

  const region = REGIONS.find(r => r.id === answers.region);
  if (region) {
    add(answers.path === 'physician' ? 'regionTitlePhysician' : 'regionTitle', tr(region.labelKey));
  }

  if (answers.path !== 'physician') {
    if (answers.wage !== null && answers.wage !== undefined && answers.wage !== '') {
      add('wageTitle', `$${answers.wage}/hr`);
    }
    const experience = EXPERIENCE_OPTIONS.find(o => o.id === answers.experience);
    add('experienceTitle', experience ? tr(experience.labelKey) : answers.experience);

    if (answers.monthsInPosition) {
      add('monthsInPositionLabel', tr(MONTH_KEYS[answers.monthsInPosition] || answers.monthsInPosition));
    }
    if (answers.monthsInOntario) {
      add('monthsInOntarioLabel', tr(MONTH_KEYS[answers.monthsInOntario] || answers.monthsInOntario));
    }
  }

  add('educationTitle', EDUCATION_KEYS[answers.education] ? tr(EDUCATION_KEYS[answers.education]) : answers.education);
  add('canadianCredsLabel', answers.canadianCredentials);
  if (answers.eca) {
    add('ecaLabel', answers.eca === 'yes' ? tr('yes') : answers.eca === 'no' ? tr('no') : tr('notYet'));
  }
  add('languageTitle', CLB_KEYS[answers.clb] ? tr(CLB_KEYS[answers.clb]) : answers.clb);
  if (answers.bothLanguages) {
    add('bothLanguagesLabel', answers.bothLanguages === 'both' ? tr('bothLanguagesYes') : tr('no'));
  }
  add('statusLabel', STATUS_KEYS[answers.legalStatus] ? tr(STATUS_KEYS[answers.legalStatus]) : answers.legalStatus);
  add('earningsLabel', EARNINGS_KEYS[answers.earnings] ? tr(EARNINGS_KEYS[answers.earnings]) : answers.earnings);

  return pairs;
}

/** Same idea for the employer checker. */
export function buildEmployerTranscript(answers, language = 'en') {
  const tr = translator(language);
  const tier = EMPLOYER_LOCATION_TIERS.find(t => t.id === answers.location);
  const params = tier
    ? { revenueFloor: tier.revenueFloor, minEmployees: tier.minCanadianOrPrEmployees }
    : {};

  const pairs = [];
  for (const question of EMPLOYER_QUESTIONS) {
    const answerId = answers[question.id];
    if (!answerId) continue;
    const option = question.options.find(o => o.id === answerId);
    pairs.push({
      q: tr(question.promptKey, question.dynamic ? params : undefined),
      a: option ? tr(option.labelKey) : String(answerId),
    });
  }
  return pairs;
}
